<template>
	<div>
		<div class="row">
			<div class="col-8 mb-4">
				<router-link
					class="float-left btn btn-secondary mr-3 flex flex-row items-center"
					v-if="loaded && sessionType.ttsessiontype.ttsessiontypeparent_id"
					:to="`/timetables/session-types/${sessionType.ttsessiontype.ttsessiontypeparent_id}`"
				>
					<b-icon icon="arrow-left-circle" class="mr-2" />
					{{ $t('back-to-parent') }}
				</router-link>
				<h1 class="float-left" v-if="isNew">{{ $t("add-session-type") }}</h1>
				<h1 class="float-left" v-else-if="loaded">
					{{ sessionType.ttsessiontype.ttsessiontype_name }}
				</h1>
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>
			<div class="col-4 mb-4">
				<button class="btn btn-primary float-right ml-4" @click="finishEditing" v-if="editing">
					{{ $t("save") }}
				</button>
				<button class="btn btn-primary float-right" @click="startEditing" v-else>
					{{ $t("edit") }}
				</button>
				<button class="btn btn-outline-danger float-right" @click="$router.go()" v-if="editing">{{ $t("cancel") }}</button>
			</div>
		</div>

		<div class="row" v-if="success">
			<div class="col-12">
				<b-alert variant="success" show
					>{{ $t("saved") }}
					<router-link to="/timetables/session-types">{{
						$t("back-to-session-types")
					}}</router-link></b-alert
				>
			</div>
		</div>

		<div class="row" v-if="error">
			<div class="col-12">
				<b-alert variant="danger" show>{{ error }}</b-alert>
			</div>
		</div>

		<div class="row">
			<div class="col-12 mb-2">
				<label class="font-bold">{{ $t("name") }}</label>
				<input
					type="text"
					class="form-control"
					value=""
					:placeholder="$t('enter-session-type-name')"
					v-model="sessionType.ttsessiontype.ttsessiontype_name"
					v-if="editing"
				/>
				<p class="mb-0" v-else-if="loaded">
					{{ sessionType.ttsessiontype.ttsessiontype_name }}
				</p>
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-2">
				<label class="font-bold">{{ $t("colour") }}</label>

				<div class="card p-2" v-if="editing">
					<div @click="toggleColourPicker">
						<div class="float-left">
							<div class="colour-indicator float-left mr-1">
								<div class="colour-indicator-inner" :style="`background: #${sessionType.ttsessiontype.ttsessiontype_colourcode}`"></div>
							</div>
							<p class="float-left mb-0 mt-2">{{ `#${sessionType.ttsessiontype.ttsessiontype_colourcode}` }}</p>
						</div>
						<div class="float-right mt-1">
							<b-icon :icon="customColour ? 'chevron-up' : 'chevron-down'" />
						</div>
					</div>

					<div v-if="customColour" class="mt-2" style="border-top: 1px solid rgba(0,0,0,0.1);">
						<div class="row">
							<div class="col-2 flex items-center justify-center mt-3 mb-3" v-for="colour in colours" v-bind:key="colour.id">
								<div :class="`${selectedColour && selectedColour.id === colour.id ? `colour-indicator active` : `colour-indicator`}`">
									<div @click="() => selectColour(colour)" class="colour-indicator-inner" :style="`background-color: #${colour.ttsessiontypecolour_hex};`"></div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-else-if="loaded">
					<div class="colour-indicator float-left mr-1">
						<div class="colour-indicator-inner" :style="`background: #${sessionType.ttsessiontype.ttsessiontype_colourcode}`"></div>
					</div>
					<p class="float-left mb-0 mt-2">{{ `#${sessionType.ttsessiontype.ttsessiontype_colourcode}` }}</p>
				</div>

				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-2">
				<label class="font-bold">{{ $t("tags") }}</label>
				<TagPicker
					v-if="editing"
					:tags="sessionType.ttsessiontype.tags"
				/>
				<div v-else-if="loaded" style="min-height: 18px">
					<TagReadonly
						:tags="sessionType.ttsessiontype.tags"
					/>
				</div>
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-2">
				<div class="row">
					<div class="col-12 col-md-6 mb-2">

						<div v-if="loaded">
							<button class="btn btn-primary mb-2" :disabled="!editing" @click="sessionType.ttsessiontype.ttsessiontype_usesclinicalcapacity=false" v-if="sessionType.ttsessiontype.ttsessiontype_usesclinicalcapacity">
								<b-icon icon="check-circle" scale="0.8" /> {{ $t("uses-seats") }}
							</button>
							<button class="btn btn-secondary mb-2" @click="sessionType.ttsessiontype.ttsessiontype_usesclinicalcapacity=true" v-else>
								<b-icon icon="x-circle" scale="0.8" /> {{ $t("doesnt-use-seats") }}
							</button>
						</div>
						<div class="skeleton midnight h16 w-half" v-else></div>

						<div v-if="sessionType.ttsessiontype.ttsessiontype_capacitymultiplier>=1">
							<label class="font-bold">{{ $t("students-per-seat") }}</label>
							<div v-if="editing">
								<b-form-select v-model="sessionType.ttsessiontype.ttsessiontype_capacitymultiplier">
									<b-form-select-option value="1.0">1</b-form-select-option>
									<b-form-select-option value="2.0">2</b-form-select-option>
									<b-form-select-option value="3.0">3</b-form-select-option>
									<b-form-select-option value="4.0">4</b-form-select-option>
									<b-form-select-option value="5.0">5</b-form-select-option>
									<b-form-select-option value="6.0">6</b-form-select-option>
									<b-form-select-option value="7.0">7</b-form-select-option>
									<b-form-select-option value="8.0">8</b-form-select-option>
									<b-form-select-option value="9.0">9</b-form-select-option>
									<b-form-select-option value="10.0">10</b-form-select-option>
								</b-form-select>
							</div>
							<p class="mb-0" v-else-if="loaded">
								{{ sessionType.ttsessiontype.ttsessiontype_capacitymultiplier }}
							</p>
							<div class="skeleton midnight h16 w-half" v-else></div>
						</div>

					</div>
				</div>
			</div>

			<div class="col-12 mb-2">
				<label class="font-bold">{{ $t("peers-visible-to-one-another") }}</label>
				<YesNoToggle 
					v-if="loaded" 
					v-model="sessionType.ttsessiontype.ttsessiontype_peersvisible"
					:label="$t('peers-visible-to-one-another')"
					:srOnly="true"
					:disabled="!editing"
				/>
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-4">
				<div class="clearfix mb-4">
					<label class="font-bold float-left">{{ $t("child-session-types") }}</label>
					<button
						class="float-right btn btn-secondary"
						@click="addChildSessionType"
						v-if="editing"
					>
						<b-icon icon="plus" />
						{{ $t("add") }}
					</button>
				</div>

				<div v-if="loaded && sessionType.ttsessiontype_children.length > 0" class="list-group">
					<div class="list-group-item d-none d-sm-inline">
						<div class="row">
							<div class="col-12 col-sm-6 font-bold">{{ $t("name") }}</div>
							<div class="col-12 col-sm-2 font-bold">{{ $t("uses-seats") }}</div>
							<div class="col-12 col-sm-2 font-bold">{{ $t("students-per-seat") }}</div>
						</div>
					</div>
					<div class="list-group-item" v-for="(childType) in sessionType.ttsessiontype_children" v-bind:key="childType.ttsessiontype_id">
						<div class="row" v-if="editing">
							<div class="col-12 col-sm-6 mb-1">
								<span class="font-bold d-inline d-sm-none">{{ $t("name") }}</span>
								<b-form-input v-model="childType.ttsessiontype_name" placeholder="Enter name"></b-form-input>
							</div>
							<div class="col-12 col-sm-2 mb-1">
								<span class="font-bold d-inline d-sm-none">{{ $t("uses-seats") }}</span>
								<button class="btn btn-primary block mb-2" @click="childType.ttsessiontype_usesclinicalcapacity = !childType.ttsessiontype_usesclinicalcapacity" v-if="childType.ttsessiontype_usesclinicalcapacity">
									<b-icon icon="check-circle" scale="0.8" /> {{ $t("uses-seats") }}
								</button>
								<button class="btn btn-secondary block mb-2" @click="childType.ttsessiontype_usesclinicalcapacity = !childType.ttsessiontype_usesclinicalcapacity" v-else>
									<b-icon icon="x-circle" scale="0.8" /> {{ $t("doesnt-use-seats") }}
								</button>
							</div>
							<div class="col-12 col-sm-2 mb-1" v-if="childType.ttsessiontype_clinicalcapacitymultiplier > 0">
								<span class="font-bold d-inline d-sm-none">{{ $t("students-per-seat") }}</span>
								<b-form-select v-model="childType.ttsessiontype_capacitymultiplier">
									<b-form-select-option value="1">1</b-form-select-option>
									<b-form-select-option value="2">2</b-form-select-option>
									<b-form-select-option value="3">3</b-form-select-option>
									<b-form-select-option value="4">4</b-form-select-option>
									<b-form-select-option value="5">5</b-form-select-option>
									<b-form-select-option value="6">6</b-form-select-option>
									<b-form-select-option value="7">7</b-form-select-option>
									<b-form-select-option value="8">8</b-form-select-option>
									<b-form-select-option value="9">9</b-form-select-option>
									<b-form-select-option value="10">10</b-form-select-option>
								</b-form-select>
							</div>
							<!-- <div class="col-12 col-sm-2">
								<span class="font-bold d-inline d-sm-none">{{ $t("patient-slots") }}</span>
								<input v-if="editing" class="form-control" type="number" v-model="sessionType.ttpatientslots[index].ttpatientslot_slots" />
								<template v-else>{{ sessionType.ttpatientslots[index].ttpatientslot_slots }}</template>
							</div> -->
						</div>
						<router-link class="link-bare" :to="`/timetables/session-types/${childType.ttsessiontype_id}`" v-else>
							<div class="row">
								<div class="col-12 col-sm-6 mb-1">
									<span class="font-bold d-inline d-sm-none">{{ $t("name") }}</span>
									{{ childType.ttsessiontype_name }}
									<b-icon icon="arrow-right-circle" />
								</div>
								<div class="col-12 col-sm-2 mb-1">
									<span class="font-bold d-inline d-sm-none">{{ $t("uses-seats") }}</span>
									{{ childType.ttsessiontype_usesclinicalcapacity > 0 ? "Yes" : "No" }}
								</div>
								<div class="col-12 col-sm-2 mb-1">
									<span class="font-bold d-inline d-sm-none">{{ $t("students-per-seat") }}</span>
									{{ childType.ttsessiontype_capacitymultiplier }}
								</div>
								<!-- <div class="col-12 col-sm-2">
									<span class="font-bold d-inline d-sm-none">{{ $t("patient-slots") }}</span>
									<input v-if="editing" class="form-control" type="number" v-model="sessionType.ttpatientslots[index].ttpatientslot_slots" />
									<template v-else>{{ sessionType.ttpatientslots[index].ttpatientslot_slots }}</template>
								</div> -->
							</div>
						</router-link>
					</div>
				</div>

				<div class="card text-center" v-else-if="loaded">
					<div class="card-body">
						<p class="mb-0">{{ $t('none') }}</p>
					</div>
				</div>

				<table v-else class="table">
					<tr v-for="item in Array(5)" v-bind:key="`key-${item}`">
						<td>
							<div class="skeleton midnight h16 w-half"></div>
						</td>
						<td>
							<div class="skeleton midnight h16 w-half"></div>
						</td>
					</tr>
				</table>
			</div>

			<div class="col-12 mb-8">
				<label class="font-bold mb-4">{{ $t("patient-slots") }}</label>

				<div v-if="loaded && templates.length > 0" class="list-group">
					<template v-for="template in templates">
						<div class="list-group-item" v-bind:key="'hdr' + template.tttemplate_id">
							<div class="row">
								<div class="col-12 col-sm-6 mb-1">
									<span class="font-bold">{{ template.tttemplate_name }}</span>
								</div>
								<div class="col-12 col-sm-3 mb-1">
									<span class="font-bold">{{ template.tttemplate_timefrom.split(" ")[1] }}</span>
								</div>
								<div class="col-12 col-sm-3 mb-1">
									<span class="font-bold">{{ template.tttemplate_timeto.split(" ")[1] }}</span>
								</div>
							</div>
						</div>
						<div class="list-group-item" v-bind:key="template.tttemplate_id">
							<template v-for="(patientslot, pskey) in sessionType.ttpatientslots">
								<div class="row mb-2" :key="template.tttemplate_id + '-' + pskey" v-if="patientslot.tttemplate_id == template.tttemplate_id">
									<div class="col-md-6">
										{{ $t("appointment-time") }}
									</div>
									<div class="col-md-3">
										<input v-if="editing" class="form-control" type="time" v-model="patientslot.ttpatientslot_time" />
										<template v-else>{{ patientslot.ttpatientslot_time }}</template>
									</div>
									<div class="col-md-3">
										<button v-if="editing" class="btn btn-danger" @click="removeAppointment(pskey)"><b-icon icon="trash" /> {{ $t('remove') }}</button>
									</div>
								</div>
							</template>
							<button v-if="editing" class="btn btn-outline-secondary float-right" @click="addAppointment(template.tttemplate_id)"><b-icon icon="plus" /> {{ $t('add') }}</button>
						</div>
					</template>
				</div>

				<table v-else class="table">
					<tr v-for="item in Array(5)" v-bind:key="`key-${item}`">
						<td>
							<div class="skeleton midnight h16 w-half"></div>
						</td>
						<td>
							<div class="skeleton midnight h16 w-half"></div>
						</td>
					</tr>
				</table>
			</div>

			<div class="col-12 mb-4">
				<label class="float-left font-bold mt-2 mb-0">{{ $t("rooms") }}</label>
				<button
					class="float-right btn btn-secondary"
					@click="openRoomSelect"
					v-if="editing"
				>
					<b-icon icon="plus" />
					{{ $t("add") }}
				</button>
			</div>

			<div class="col-12 mb-8" v-if="(sessionType.ttrooms && sessionType.ttrooms.length > 0)">
				<div class="list-group">
					<div class="list-group-item" v-for="room in sessionType.ttrooms" v-bind:key="room.id">
						<p class="mb-0">{{ room.name }}</p>
					</div>
				</div>
			</div>

			<div class="col-12 mb-8" v-else>
				<div class="card text-center">
					<div class="card-body">
						<p class="mb-0">{{$t("no-rooms-added")}}</p>
					</div>
				</div>
			</div>

			<div class="col-12 mb-8">
				<label class="font-bold mb-4">{{ $t("teachers") }}</label>
				<button class="float-right btn btn-secondary mx-1" @click="addTeacherRota()" v-if="editing"><b-icon icon="plus" />{{ $t("add") }}</button>
				<div class="list-group mt-1">
					<div class="list-group mt-1" v-for="(teacherrota, itemno) in teacherrotas" :key="itemno">
						<div class="list-group-item">
							<div class="row">
								<div class="col-md-4">
									{{$t('from')}} <input type="date" v-model="teacherrota.datefrom" class="form-control"  v-if="editing && teacherrota.id==null"><p class="mb-0" v-else-if="loaded">
									{{ $datetime.getLocaleStringFromISODate(teacherrota.datefrom) }}</p>
								</div>
								<div class="col-md-4">
									{{$t('to')}} <input type="date" v-model="teacherrota.dateto" class="form-control"  v-if="editing && teacherrota.id==null"><p class="mb-0" v-else-if="loaded">
									{{ $datetime.getLocaleStringFromISODate(teacherrota.dateto) }}</p>
								</div>
							</div>						</div>
						<div class="list-group-item">
							<div class="row">
								<div class="col-md-2">
									<span class="font-bold">{{ $t("room") }}</span>
								</div>
								<div class="col-md-2">
									<span class="font-bold">{{ $t("day") }}</span>
								</div>
								<div class="col-md-2">
									<span class="font-bold">{{ $t("period") }}</span>
								</div>
								<div class="col-md-6">
									<span class="font-bold">{{ $t("teacher") }}</span>
									<button v-if="editing" class="btn btn-secondary float-right" @click="addTeacherTemplate(teacherrota)"><b-icon icon="plus" /> {{ $t('add') }}</button>
								</div>
							</div>
						</div>
						<template v-for="(teacher, tkey) in teacherrota.rules">
							<div class="list-group-item" v-bind:key="tkey">
								<div class="row">
									<div class="col-md-2">
										<select v-if="editing" class="form-control"  v-model="teacher.ttroom_id">
											<option value="">{{$t('select')}}</option>
											<option v-for="room in rooms" :key="room.id" :value="room.id">{{ room.name }}</option>
										</select>
										<template v-else>
											<template v-for="room in rooms">
												<template v-if="room.id == teacher.ttroom_id">{{ room.name }}</template>
											</template>
										</template>
									</div>
									<div class="col-md-2">
										<select v-if="editing" class="form-control"  v-model="teacher.dayofweek">
											<option value="">{{$t('select')}}</option>
											<option v-for="index in 7" :key="`key-${index}`" :value="index">{{ $t('dayname-long-' + (index)) }}</option>
										</select>
										<template v-else>{{ $t('dayname-long-' + teacher.dayofweek) }}</template>
									</div>
									<div class="col-md-2">
										<select v-if="editing" class="form-control" v-model="teacher.tttemplate_id">
											<option value="">{{$t('select')}}</option>
											<option v-for="template in templates" :key="template.tttemplate_id" :value="template.tttemplate_id">{{ template.tttemplate_name }}</option>
										</select>
										<template v-else>
											<template v-for="template in templates">
												<template v-if="template.tttemplate_id == teacher.tttemplate_id">{{template.tttemplate_name}}</template>
											</template>
										</template>
									</div>
									<div class="col-md-4">
										<template v-if="editing">
											<div
												class="pseudo-input"
											>
												<input
													type="search"
													class="input-bare w-full flex-1"
													data-cy="message-user-search"
													:placeholder="$t('teacher')"
													v-if="!teacher.users.length"
													@click="openTeacherModal(teacher.users)"
												/>
												<span
													class="badge badge-secondary badge-sm mr-2 mb-2"
													v-for="(user, index) in teacher.users"
													v-bind:key="index"
												>
													{{ user.displayName }}
													<a @click="removeTeacher(teacher.users, index)"><b-icon icon="x"></b-icon></a>
												</span>
												<span
													class="badge badge-primary badge-sm mr-2 mb-2"
													v-if="teacher.users.length"
													@click="openTeacherModal(teacher.users)"
												>
													<b-icon icon="plus"></b-icon> {{ $t("add") }}
												</span>
												<span
													class="badge badge-danger badge-sm mr-2 mb-2"
													v-if="teacher.users.length"
													@click="teacher.users=[]"
												>
													<b-icon icon="x"></b-icon> {{ $t('clear-all') }}
												</span>
											</div>
										</template>
										<template v-else>
											<span
												class="badge badge-secondary badge-sm mr-2 mb-2"
												v-for="(user, index) in teacher.users"
												v-bind:key="index"
											>
												{{ user.displayName }}
											</span>
										</template>
									</div>
									<div class="col-md-2">
										<button v-if="editing" class="btn btn-danger" @click="removeTeacherTemplate(teacherrota, tkey)"><b-icon icon="trash" /> {{ $t('remove') }}</button>
									</div>
								</div>
							</div>
						</template>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import TagPicker from "src/components/Tags/TagPicker";
import TagReadonly from "src/components/Tags/TagReadonly";
import YesNoToggle from "src/components/Inputs/YesNoToggle"

export default {
	components: {
		TagPicker,
		TagReadonly,
		YesNoToggle,
	},
	data: function () {
		return {
			success: false,
			error: null,
			loaded: false,
			editing: false,
			rooms: [],
			templates: [],
			teachers: [],
			sessionType: {
				ttsessiontype: {
					ttsessiontype_id: null,
					ttsessiontype_name: "",
					ttsessiontype_usesclinicalcapacity: false,
					ttsessiontype_capacitymultiplier: 1,
					ttsessiontype_clinicalcapacitymultiplier: 1,
					ttsessiontype_colourcode: null,
					ttsessiontype_peersvisible: false,
					tags: []
				},
				ttsessiontype_children: [],
				ttrooms: [],
				ttpatientslots: [],
				teacherrota: []
			},
			colours: [],
			customColour: false,
		};
	},
	mounted() {
		this.getRooms();
		this.getTemplates();
		this.getColours();
		this.getTeachers();

		if (this.isNew) {
			this.loaded = true;
			this.startEditing();
		} else {
			this.editing = false;
			this.loadSessionType(this.$route.params["sessiontypeid"]);
		}
	},
	methods: {
		removeTeacherRota (index) {
			this.teacherrotas.splice(index, 1);
		},
		addTeacherRota() {
            this.sessionType.teacherrota.unshift({
                id: null,
                datefrom : null,
                dateto: null,
                rules : []
            });
          },
		addTeacherTemplate(teacherrota){
			teacherrota.rules.push({
				users: [],
				ttroom_id: null,
				tttemplate_id: null,
				dayofweek: null
			});
		},
		startEditing: function () {
			this.editing = true;
		},
		finishEditing: function () {
			this.saveSessionType();
		},
		loadSessionType: function (sessionTypeId) {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/session-setup/sessiontype/get`, {
				data: {
					ttsessiontype_id: sessionTypeId,
				},
			}).then((res) => {
				if (res.status === "SUCCESS") {
					this.sessionType = res.result;
					this.sessionTypeOriginal = res.result;

					this.loaded = true;
				} else {
					if (res.error_message) {
						this.error = res.error_message;
					} else {
						this.error = this.$t("failed-load-session-type");
					}
				}
			});
		},
		saveSessionType: function () {
			this.$store.commit("showBlockingSpinner");
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/session-setup/sessiontype/save`, {
				method: "post",
				data: this.sessionType
			})
			.then((res) => {
				this.$store.commit("hideBlockingSpinner");
				if (res.status === "SUCCESS") {

					this.sessionType = res.result;

					this.error = null;
					this.success = true;
					this.editing = false;
				} else {
					if (res.error_message) {
						this.error = res.error_message;
					} else {
						this.error = "There was a problem saving the session type.";
					}
				}
			})
			.catch((error) => {
			this.$store.commit("hideBlockingSpinner");
				this.$log.error.call(this, error);
				this.error = "There was a problem saving the session type.";
			});
		},
		getRooms: function () {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/ttroom/listall`).then((res) => {
				if (res.status === "SUCCESS") {
					this.rooms = res.results;
				}
			});
		},
		getTemplates: function() {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/tttemplate/list`)
				.then(res => {
					if (res.status === "SUCCESS") {
						this.templates = res.results;

						this.sessionType.ttpatientslots = res.results.map(template => ({
							tttemplate_id: template.id,
							ttpatientslot_slots: 1
						}));
					} else {
						if (res.error_message) {
							this.error = res.error_message;
						} else {
							this.error = this.$t("failed-load-templates");
						}
					}
				})
				.catch(() => {
					this.error = this.$t("failed-load-templates");
				})
		},
		getTeachers: function() {
			this.$users.getTeachers.call(this).then(json => {
				this.teachers = json;
			});
		},
		getTemplateData: function(templateId) {
			return this.templates.find(template => template.id === templateId);
		},
		openRoomSelect: function () {
			const self = this;
			this.$store.commit("openModal", {
				type: "selector",
				title: self.$t('select-room'),
				selectCallback: function (selection) {
					const isSelected = self.sessionType.ttrooms.find((room, i) => {
						if (room.id === selection.id) {
							self.$delete(self.sessionType.ttrooms, i);
							return true;
						}
					});
					if (!isSelected) {
						self.sessionType.ttrooms.push(selection);
					}
				},
				selected: this.sessionType.ttrooms,
				data: this.rooms,
				//captionKey: "ttroom_name",
				captionKey: "name"
			});
		},
		getColours: function() {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/ttsessiontypecolour/get`)
				.then(res => {
					if (res.status === "SUCCESS") {
						this.colours = res.results.filter(colour => colour.ttsessiontypecolour_hex !== "");

						if (!this.sessionType.ttsessiontype.ttsessiontype_colourcode) {
							this.sessionType.ttsessiontype.ttsessiontype_colourcode = this.colours[0].ttsessiontypecolour_hex;
						}
					} else {
						if (res.error_message) {
							this.error = res.error_message;
						} else {
							this.error = "Failed to load colours";
						}
					}
				})
		},
		selectColour: function(colour) {
			this.sessionType.ttsessiontype.ttsessiontype_colourcode = colour.ttsessiontypecolour_hex;
			this.customColour = false;
		},
		toggleColourPicker: function() {
			this.customColour = !this.customColour;
		},
		enableAssist: function() {
			// this.sessionType.ttsessiontype_children = [
			// 	{
			// 		ttsessiontype_name: `${this.sessionType.ttsessiontype.ttsessiontype_name} Assist`,
			// 		ttsessiontype_useclinicalcapacity: false,
			// 		ttsessiontype_capacitymultiplier: "1.0",
			// 		ttsessiontype_clinicalcapacitymultiplier: "1.0"
			// 	}
			// ];
		},
		disableAssist: function() {
			// this.sessionType.ttsessiontype_children = [];
		},
		addChildSessionType: function() {
			this.sessionType.ttsessiontype_children.push({
					ttsessiontype_name: ``,
					ttsessiontype_useclinicalcapacity: false,
					ttsessiontype_capacitymultiplier: "0",
					ttsessiontype_clinicalcapacitymultiplier: "0"
				});
		},
		toggleChildMultiplier: function(index) {
			this.sessionType.ttsessiontype_children[index].ttsessiontype_clinicalcapacitymultiplier = this.sessionType.ttsessiontype_children[index].ttsessiontype_clinicalcapacitymultiplier > 0 ? 0 : 1;
			// const updated =	this.sessionType.ttsessiontype_children.map((item, i) => {
			// 	if (i === index) {
			// 		return {
			// 			...item,

			// 		}
			// 	}
			// })
		},
		addAppointment(tttemplate_id){
			this.sessionType.ttpatientslots.push({
				ttpatientslot_id: null,
				ttpatientslot_time: null,
				tttemplate_id: tttemplate_id
			});
		},
		removeAppointment(index){
			this.sessionType.ttpatientslots.splice(index, 1);
		},
		openTeacherModal(teachers) {
			const self = this;
			this.$store.commit("openModal", {
				type: "selector",
				title: this.$t("teacher"),
				selectCallback: function(selection) {
					const isSelected = teachers.find((teacher, i) => {
						if (teacher.id === selection.id) {
							self.$delete(teachers, i);
							return true;
						}
					});
					if (!isSelected) {
						teachers.push(selection);
					}
					self.recipientsError = false;
				},
				selected: teachers,
				data: this.teachers,
				captionKey: "displayName"
			});
		},
		removeTeacherTemplate(teacherrota, index){
			teacherrota.rules.splice(index, 1);
			return true;
		},
		removeTeacher(users, selection) {
			users.splice(selection, 1);
			return true;
		}
	},
	computed: {
		isNew: function () {
			return !this.$route.params["sessiontypeid"];
		},
		hasAssist: function () {
			return (
				this.sessionType.ttsessiontype_children &&
				this.sessionType.ttsessiontype_children.length > 0
			);
		},
		hydratedSessionTypeRooms: function() {
			return this.sessionType.ttrooms.map(room => this.rooms.find(r => r.ttroom_id === room.id));
		},
		/*teacherrotas() {
			return Object.values(this.sessionType.teacherrota);
		}*/
		teacherrotas: {
			get() {
				return this.sessionType.teacherrota;
			},
			set(newTeacherrotas) {
				this.sessionType.teacherrota = newTeacherrotas;
			},
   	 	},
	},
};
</script>
<style>
.teacherrota-fade-enter-active,
.teacherrota-fade-leave-active {
  transition: opacity 1.5s;
}

.teacherrota-fade-enter,
.teacherrota-fade-leave-to {
  opacity: 0;
}

</style>

